import { useQuery } from "@tanstack/react-query";
import { authenticatedGet, authenticatedFetch } from "../../api/authenticatedFetch";

const AUDIT_EXPORT_URL = "/api/v1/audit/export";
const EFFECTIVE_ACCESS_URL = "/api/v1/audit/effective-access";

// cleanParams drops undefined/null/"" entries so neither authenticatedGet's
// URLSearchParams nor our own query-string builder ever emits e.g.
// "event_type=undefined" as a literal filter value.
const cleanParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
};

const buildQueryString = (params = {}) => new URLSearchParams(cleanParams(params)).toString();

// getAuditEvents fetches the D3 audit-evidence log (Sub-project D3, Task 2/5):
// a tenant-wide, time-ranged, filterable export of audit events. `params` are
// passed straight through as backend query keys (from, to, event_type,
// object_type, severity, limit -- all RFC3339/plain strings, all optional).
// Returns { events, truncated } with events always a (possibly empty) array.
export const getAuditEvents = async (params = {}) => {
  const res = await authenticatedGet(AUDIT_EXPORT_URL, { format: "json", ...cleanParams(params) });
  const body = res?.audit_export ?? res;
  return { events: body?.events ?? [], truncated: !!body?.truncated };
};

// getEffectiveAccess fetches the D3 "who can do what" auditor snapshot
// (Sub-project D3, Task 4/5): every user's and service account's effective
// access, tenant-owner/access-review posture, and SoD findings.
export const getEffectiveAccess = async () => {
  const res = await authenticatedGet(EFFECTIVE_ACCESS_URL, { format: "json" });
  return res?.effective_access?.report ?? res?.report ?? res;
};

// --- react-query hooks ---

export function useAuditEvents(params = {}) {
  return useQuery(["audit-events", cleanParams(params)], () => getAuditEvents(params), {
    refetchOnWindowFocus: false,
  });
}

export function useEffectiveAccess() {
  return useQuery(["effective-access"], getEffectiveAccess, {
    refetchOnWindowFocus: false,
  });
}

// --- blob download helpers ---
//
// The CSV endpoints return a raw attachment, not JSON -- authenticatedGet
// always calls response.json(), so these go through authenticatedFetch
// directly (it still handles auth headers + 401/403 refresh-retry) and read
// the body as a Blob. triggerDownload then synthesizes a click on a hidden
// <a download> to hand the blob to the browser's normal save flow.
const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const downloadAuditCsv = async (params = {}) => {
  const qs = buildQueryString({ format: "csv", ...params });
  const res = await authenticatedFetch(`${AUDIT_EXPORT_URL}?${qs}`, { method: "GET" });
  const blob = await res.blob();
  triggerDownload(blob, "audit-events.csv");
};

export const downloadEffectiveAccessCsv = async () => {
  const res = await authenticatedFetch(`${EFFECTIVE_ACCESS_URL}?format=csv`, { method: "GET" });
  const blob = await res.blob();
  triggerDownload(blob, "effective-access.csv");
};

// downloadJson serializes an already-fetched payload (the audit events page
// already has `events`/`report` in hand via react-query) to a formatted
// .json file, so the JSON export button doesn't need a second network round
// trip -- it just re-shapes what's already on screen.
export const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  triggerDownload(blob, filename);
};
